import type { StoredUpdateCycle, UpdateCycle } from "#app/lib/project-updates";

// The Follow up cycle ladder, on its own so the prototype stores can read it
// without a value import of `project-updates` (which reaches `lib/data`, and a
// store loaded during that module's evaluation would find its exports in the
// temporal dead zone). Type-only imports are erased, so this file has no
// runtime edge back.

/** The one cycle ladder every Follow up offers — a Project's own cycle, an
 *  Account's or a Contact's window. Mirrors `PROJECT_CYCLE_PRESETS` in the
 *  issues resolver, which rejects anything off it. Shorter than the Stage
 *  presets on purpose: a Stage pin is a policy, a Follow up cycle is one rep
 *  saying "watch this one closely". */
export const FOLLOW_UP_CYCLE_PRESETS: ReadonlyArray<UpdateCycle> = [
  { amount: 1, step: "days" },
  { amount: 3, step: "days" },
  { amount: 1, step: "weeks" },
  { amount: 2, step: "weeks" },
  { amount: 1, step: "months" },
  { amount: 3, step: "months" },
];

/** What the Start dialog pre-answers with when no rung below hands one down. */
export const DEFAULT_FOLLOW_UP_CYCLE: UpdateCycle = { amount: 2, step: "weeks" };

/** The ladder rung as one or two short words for a segmented control —
 *  "Day", "3 days", "Week", "2 weeks" — under a "Repeat every" label that
 *  supplies the "every". `formatUpdateCycle` keeps the full sentence for
 *  rows and readouts that stand alone. */
export function formatUpdateCycleShort(cycle: UpdateCycle): string {
  const step = cycle.step.replace(/s$/, "");
  if (cycle.amount === 1) return step.charAt(0).toUpperCase() + step.slice(1);
  return `${cycle.amount} ${cycle.step}`;
}

/** A cycle as a stable key — Select values, React keys, preset lookups. */
export const cycleKey = (cycle: StoredUpdateCycle): string =>
  `${cycle.amount}-${cycle.step}`;

export function findFollowUpCyclePreset(key: string): UpdateCycle | undefined {
  return FOLLOW_UP_CYCLE_PRESETS.find((preset) => cycleKey(preset) === key);
}

const APPROX_DAYS: Record<UpdateCycle["step"], number> = {
  hours: 1 / 24,
  days: 1,
  weeks: 7,
  months: 30,
  years: 365,
};

/** The ladder rung nearest an arbitrary cycle — what the Start dialog
 *  pre-answers with when the inherited default (a Workflow's "Every 30
 *  days", an automation's odd pin) is not itself a preset. */
export function nearestFollowUpCyclePreset(cycle: UpdateCycle): UpdateCycle {
  const days = cycle.amount * APPROX_DAYS[cycle.step];
  let best = DEFAULT_FOLLOW_UP_CYCLE;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const preset of FOLLOW_UP_CYCLE_PRESETS) {
    const distance = Math.abs(preset.amount * APPROX_DAYS[preset.step] - days);
    if (distance < bestDistance) {
      best = preset;
      bestDistance = distance;
    }
  }
  return best;
}
